import { AccountResource } from '@web/types/resources/AccountResource';

const ThirdPartyServices = {
  init() {
    const { global, user } = window.Clearstream;

    if (user) {
      window.Intercom &&
        window.Intercom('boot', {
          // https://developers.intercom.com/installing-intercom/docs/javascript-api-attributes-objects
          app_id: global.keys.intercom,
          user_id: user.id,
          user_hash: user.intercomHash,
          session_duration:
            window.Clearstream.global.sessionLifetime * 60 * 1000,
        });
    } else {
      window.Intercom &&
        window.Intercom('boot', {
          app_id: global.keys.intercom,
        });
    }
  },

  reportRouteChange(fullPath: string) {
    // Every time we change the URL, we need to let Intercom know.
    // https://www.intercom.com/help/en/articles/170-integrate-intercom-in-a-single-page-app
    window.Intercom &&
      window.Intercom('update', {
        last_request_at: Math.ceil(new Date().getTime() / 1000),
      });

    // https://bingadsuet.azurewebsites.net/SPA_Index.html
    window.uetq &&
      window.uetq.push('event', 'page_view', { page_path: fullPath });

    // https://developers.google.com/analytics/devguides/collection/gtagjs/single-page-applications#measure_virtual_pageviews
    window.gtag && window.gtag('set', 'page_path', fullPath);
    window.gtag && window.gtag('event', 'page_view');

    // https://developers.openai.com/ads/measurement-pixel
    window.oaiq && window.oaiq('measure', 'page_viewed', { type: 'contents' });
  },

  reportLogout() {
    // We must clear the Intercom session on logout.
    // https://www.intercom.com/help/en/articles/16845-how-do-i-end-a-session
    window.Intercom && window.Intercom('shutdown');
  },

  reportLead({ email, name }: { email: string; name: string }) {
    // Cometly
    window.comet &&
      window.comet('lead_generated', {
        email,
        full_name: name,
        user_agent: window.navigator.userAgent,
        // https://help.cometly.com/en/articles/11788368-event-source-url-for-meta-conversion-api
        url: 'https://clearstream.io',
      });

    // AdRoll
    try {
      window.__adroll &&
        window.__adroll.track('pageView', { segment_name: 'bf32aeef' });
    } catch (err) {
      //
    }

    // Twitter
    window.twq &&
      window.twq('event', 'tw-oamkn-onebo', {
        email_address: email,
        // phone_number: null,
      });

    // OpenAI ads measurement pixel
    window.oaiq &&
      window.oaiq('measure', 'lead_created', {
        type: 'customer_action',
      });
  },

  reportTapTagsLead({ email, name }: { email: string; name: string }) {
    // Cometly
    window.comet &&
      window.comet(
        // This event name is not specific enough, but it's been explicitly requested
        // by marketing because it's one of the standard conversation events.
        'add_payment_info',
        {
          email,
          full_name: name,
          user_agent: window.navigator.userAgent,
          // https://help.cometly.com/en/articles/11788368-event-source-url-for-meta-conversion-api
          url: 'https://clearstream.io',
        },
      );

    // OpenAI ads measurement pixel
    window.oaiq &&
      window.oaiq('measure', 'lead_created', {
        type: 'customer_action',
      });
  },

  reportSignupUsingAccount(account: AccountResource) {
    this.reportSignup({
      conversionValue: account.billing.nextChargeAmount,
      accountId: account.id,
      userId: account.user.id,
      userEmail: account.user.email,
    });
  },

  reportSignup({ accountId, conversionValue, userId, userEmail }) {
    // Google Analytics & Google Adwords
    // We need to set these up based on the data returned
    // after the sign up, since they weren't available
    // on page load.
    window.gtag &&
      window.gtag('set', {
        user_id: userId,
        dimension2: userId,
        dimension3: accountId,
      });

    // Google Analytics
    window.gtag &&
      window.gtag('event', 'purchase', {
        transaction_id: signupTransactionId(accountId),
        value: conversionValue,
        currency: 'USD',
      });

    // Google Ads
    window.gtag &&
      window.Clearstream.global.keys.google.ads.conversionSendTo &&
      window.gtag('event', 'conversion', {
        send_to: window.Clearstream.global.keys.google.ads.conversionSendTo,
        value: conversionValue,
        currency: 'USD',
        transaction_id: signupTransactionId(accountId),
      });

    // Bing
    window.uetq &&
      window.uetq.push('event', 'account_signup', {
        event_label: `account_${accountId}`,
        revenue_value: conversionValue,
        currency: 'USD',
      });

    // Bing (new)
    window.uetq &&
      window.uetq.push('event', conversionValue > 0 ? 'purchase' : 'trial', {
        revenue_value: conversionValue,
        currency: 'USD',
      });

    if (conversionValue > 0) {
      // AdRoll
      try {
        window.__adroll &&
          window.__adroll.track('pageView', { segment_name: '1b0e652d' });
      } catch (err) {
        //
      }
      window.adroll &&
        window.adroll.track('purchase', {
          order_id: accountId,
          currency: 'USD',
          conversion_value: conversionValue,
        });
    } else {
      // AdRoll
      try {
        window.__adroll &&
          window.__adroll.track('pageView', { segment_name: '250d2dd3' });
      } catch (err) {
        //
      }
    }

    if (conversionValue > 0) {
      // Twitter
      window.twq &&
        window.twq('event', 'tw-oamkn-oneas', {
          value: conversionValue,
          currency: 'USD',
          email_address: userEmail,
          // phone_number: null,
        });
    } else {
      // Twitter
      window.twq &&
        window.twq('event', 'tw-oamkn-onebp', {
          email_address: userEmail,
          // phone_number: null,
        });
    }

    // LinkedIn
    window.lintrk &&
      window.Clearstream.global.keys.linkedin.conversionId &&
      window.lintrk('track', {
        conversion_id: window.Clearstream.global.keys.linkedin.conversionId,
      });

    // Facebook
    window.fbq &&
      window.fbq('track', 'Subscribe', {
        value: conversionValue,
        currency: 'USD',
      });

    // OpenAI ads measurement pixel
    window.oaiq &&
      window.oaiq(
        'measure',
        conversionValue > 0 ? 'subscription_created' : 'trial_started',
        conversionValue > 0
          ? {
              type: 'plan_enrollment',
              amount: Math.round(conversionValue * 100),
              currency: 'USD',
            }
          : { type: 'plan_enrollment' },
      );
  },

  reportUpgradeFromFreePlan({ accountId, conversionValue, userEmail }) {
    // Google Analytics
    window.gtag &&
      window.gtag('event', 'purchase', {
        transaction_id: upgradeTransactionId(accountId),
        value: conversionValue,
        currency: 'USD',
      });

    // Google Ads
    window.gtag &&
      window.Clearstream.global.keys.google.ads.conversionSendTo &&
      window.gtag('event', 'conversion', {
        send_to: window.Clearstream.global.keys.google.ads.conversionSendTo,
        value: conversionValue,
        currency: 'USD',
        transaction_id: upgradeTransactionId(accountId),
      });

    // Bing
    window.uetq &&
      window.uetq.push('event', 'account_upgrade', {
        event_label: `account_${accountId}`,
        revenue_value: conversionValue,
        currency: 'USD',
      });

    // Bing (new)
    window.uetq &&
      window.uetq.push('event', 'purchase', {
        revenue_value: conversionValue,
        currency: 'USD',
      });

    // AdRoll
    try {
      window.__adroll &&
        window.__adroll.track('pageView', { segment_name: '1b0e652d' });
    } catch (err) {
      //
    }
    window.adroll &&
      window.adroll.track('purchase', {
        order_id: accountId,
        currency: 'USD',
        conversion_value: conversionValue,
      });

    // Twitter
    window.twq &&
      window.twq('event', 'tw-oamkn-oneas', {
        value: conversionValue,
        currency: 'USD',
        email_address: userEmail,
        // phone_number: null,
      });

    // Facebook
    window.fbq &&
      window.fbq('track', 'Subscribe', {
        value: conversionValue,
        currency: 'USD',
      });

    // OpenAI ads measurement pixel
    window.oaiq &&
      window.oaiq('measure', 'subscription_created', {
        type: 'plan_enrollment',
        amount: Math.round(conversionValue * 100),
        currency: 'USD',
      });
  },
};

function upgradeTransactionId(accountId: number) {
  return `acct_upgrade_${accountId}`;
}

function signupTransactionId(accountId: number) {
  return `acct_signup_${accountId}`;
}

export default ThirdPartyServices;
